import { FC, useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { getOutlets, createOutlet, updateOutlet, toggleOutlet, getOutletName } from '../../lib/scheduling';
import type { Outlet, TrafficLevel } from '../../types/scheduling';
import { showToast } from '../../hooks/useToast';
import { useRole } from '../../hooks/useRole';

const BranchManagementPage: FC = () => {
  const { isAdmin, loading: roleLoading } = useRole();
  const [outlets, setOutlets] = useState<Outlet[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    staff_required: 1,
    traffic_level: 'medium' as TrafficLevel,
    require_high_performer: false,
    location: ''
  });

  useEffect(() => {
    loadOutlets();
  }, []);

  const loadOutlets = async () => {
    setLoading(true);
    try {
      const data = await getOutlets(true); // include inactive
      setOutlets(data);
    } catch (error) {
      showToast('Failed to load outlets', 'error');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (editingId) {
        await updateOutlet(editingId, formData);
        showToast('Outlet updated successfully', 'success');
      } else {
        await createOutlet({ ...formData, is_active: true });
        showToast('Outlet created successfully', 'success');
      }
      
      resetForm();
      await loadOutlets();
    } catch (error) {
      showToast(`Failed to ${editingId ? 'update' : 'create'} outlet`, 'error');
      console.error(error);
    }
  };

  const handleEdit = (outlet: Outlet) => {
    setEditingId(outlet.id);
    setFormData({
      name: getOutletName(outlet),
      staff_required: outlet.staff_required,
      traffic_level: outlet.traffic_level,
      require_high_performer: outlet.require_high_performer,
      location: outlet.location || ''
    });
    setIsAdding(true);
  };

  const handleToggle = async (id: number, currentStatus: boolean) => {
    try {
      await toggleOutlet(id, !currentStatus);
      showToast(`Outlet ${!currentStatus ? 'activated' : 'deactivated'} successfully`, 'success');
      await loadOutlets();
    } catch (error) {
      showToast('Failed to toggle outlet', 'error');
      console.error(error);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      staff_required: 1,
      traffic_level: 'medium',
      require_high_performer: false,
      location: ''
    });
    setIsAdding(false);
    setEditingId(null);
  };

  const getTrafficLevelColor = (level: TrafficLevel) => {
    switch (level) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
    }
  };

  if (roleLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Outlet Management</h1>
        <p className="text-gray-600">Manage outlet locations and staffing requirements (including popup outlets)</p>
      </div>

      <div className="mb-4 sm:mb-6">
        {!isAdding ? (
          <button
            onClick={() => setIsAdding(true)}
            className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium text-sm sm:text-base"
          >
            + Add New Outlet
          </button>
        ) : (
          <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4">
              {editingId ? 'Edit Outlet' : 'Add New Outlet'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Outlet Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., SM Mall Branch"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Location
                  </label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., 123 Main St"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Staff Required *
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    required
                    value={formData.staff_required}
                    onChange={(e) => setFormData({ ...formData, staff_required: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Traffic Level *
                  </label>
                  <select
                    value={formData.traffic_level}
                    onChange={(e) => setFormData({ ...formData, traffic_level: e.target.value as TrafficLevel })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="require_high_performer"
                  checked={formData.require_high_performer}
                  onChange={(e) => setFormData({ ...formData, require_high_performer: e.target.checked })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="require_high_performer" className="ml-2 block text-sm text-gray-700">
                  Require High Performer (skill score 7+)
                </label>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-4">
                <button
                  type="submit"
                  className="w-full sm:w-auto px-4 sm:px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium"
                >
                  {editingId ? 'Update Outlet' : 'Create Outlet'}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="w-full sm:w-auto px-4 sm:px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition font-medium"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}
      </div>

      {/* Outlets Table */}
      <div className="bg-white rounded-lg shadow-md overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Outlet Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Staff Required</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Traffic Level</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {outlets.map((outlet) => (
              <tr key={outlet.id} className={!outlet.is_active ? 'bg-gray-50' : ''}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">{getOutletName(outlet)}</div>
                  {outlet.require_high_performer && (
                    <div className="text-xs text-purple-600">⭐ High Performer Required</div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {outlet.location || '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {outlet.staff_required}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${getTrafficLevelColor(outlet.traffic_level)}`}>
                    {outlet.traffic_level.toUpperCase()}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <button
                    onClick={() => handleToggle(outlet.id, outlet.is_active)}
                    className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium transition ${
                      outlet.is_active
                        ? 'bg-green-100 text-green-800 hover:bg-green-200'
                        : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                    }`}
                  >
                    {outlet.is_active ? '✓ Active' : '✕ Inactive'}
                  </button>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <button
                    onClick={() => handleEdit(outlet)}
                    className="text-blue-600 hover:text-blue-800 font-medium"
                  >
                    Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {outlets.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">No outlets found. Add your first outlet to get started.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default BranchManagementPage;
